import PersonPinCircle from '@mui/icons-material/PersonPinCircle';
import { Stack } from '@mui/material';
import MuiButton from '@mui/material/Button';
import {
  GridColDef,
  GridRenderCellParams,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import React, { useEffect, useState, ChangeEvent } from 'react';

import { ServiceAccordianFragmentProps } from './ServiceAccordianFragment.props';
import {
  LoggedIcon,
  LogoImage,
  LogoImageBox,
  TableWrapper,
  CustomFooter,
} from './ServiceAccordianFragment.styles';
import {
  ActionPermissions,
  ColorProps,
  ServiceAccordianData,
  ServiceAccordianRelation,
  SizeProps,
} from '../../../../@types';
import { v4GhostButtonSx, v4PanelToggleSx } from '../../../../components/atoms/Button';
import { DataGrid, GridAllignmentTypes } from '../../../../components/atoms/DataGrid';
import { FormControlLabel } from '../../../../components/atoms/FormControlLabel';
import { Link } from '../../../../components/atoms/Link';
import { Panel } from '../../../../components/atoms/Panel';
import { Switch } from '../../../../components/atoms/Switch';
import { TablePill, toneForPriority, toneForStatus } from '../../../../components/atoms/TablePill';
import { ServiceStatusIsClosed, GridDensityTypes } from '../../../../configs/enums';
import { isUserHasPermission } from '../../../../configs/permissions';
import { useGetServicesUnderNodeId } from '../../../../queries/service-query';
import { formatDate } from '../../../../utils/common';

export const ServiceAccordianFragment: React.FC<ServiceAccordianFragmentProps> = ({
  nodeId,
  permissions,
}) => {
  const [serviceData, setServiceData] = useState<ServiceAccordianData[]>([]);
  const [includeChildRecords, setIncludeChildRecords] = useState<boolean>(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

  const getServicesUnderNodeId = useGetServicesUnderNodeId(nodeId, includeChildRecords);

  useEffect(() => {
    if (getServicesUnderNodeId.data) {
      setServiceData(getServicesUnderNodeId.data);
    }
  }, [getServicesUnderNodeId.data]);

  useEffect(() => {
    getServicesUnderNodeId.refetch();
    // getServicesUnderNodeId is a React Query result object whose identity changes
    // on most renders; adding it here would refetch on every render rather than
    // only when the child-records toggle moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeChildRecords]);

  const columns: GridColDef[] = [
    {
      field: 'relation',
      headerName: '',
      maxWidth: 50,
      align: GridAllignmentTypes.Center,
      renderCell: (params: GridRenderCellParams) =>
        params.row.relation === ServiceAccordianRelation.Logged ? (
          <LoggedIcon />
        ) : params.row.relation === ServiceAccordianRelation.Allocated ? (
          <PersonPinCircle />
        ) : (
          ''
        ),
    },
    {
      field: 'id',
      headerName: 'Job ID',
      minWidth: 50,
      renderCell: (params: GridRenderCellParams) =>
        isUserHasPermission(ActionPermissions.Service_Edit_View_Service, permissions) ? (
          <Link href={`/services/${params.row.id}`}>{params.row.id}</Link>
        ) : (
          params.row.id
        ),
    },
    {
      field: 'subLocationName',
      headerName: 'Sub Location',
      minWidth: 100,
    },
    {
      field: 'clientHierarchy',
      headerName: 'Client/ Contract',
      filterable: false,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.logoPic && params.row.logoPic !== null) {
          return (
            <LogoImageBox>
              <LogoImage src={params.row.logoPic} alt={params.row.clientHierarchy} />
            </LogoImageBox>
          );
        } else {
          return params.row.clientHierarchy;
        }
      },
    },
    {
      field: 'priorityCode',
      headerName: 'Priority',
      minWidth: 50,
      // Same pill, tone and casing as the Services list — see
      // `serviceCellRenderer`. The colour configured per client/contract picks
      // the tone; the raw hex does not reach the screen. Uppercased because the
      // codes are not consistently cased in the data (Hso, Sup, P2, SCH).
      renderCell: (params: GridRenderCellParams) => {
        const code = params.row.priorityCode;
        if (typeof code !== 'string' || !code) return null;
        const configured =
          typeof params.row.priority?.color === 'string' ? params.row.priority.color : undefined;
        return (
          <TablePill tone={toneForPriority(code, configured)} ariaLabel={`Priority: ${code}`}>
            {code.toUpperCase()}
          </TablePill>
        );
      },
    },
    {
      field: 'serviceReference1',
      headerName: 'Service Reference #1',
      minWidth: 50,
    },
    {
      field: 'serviceStatus',
      headerName: 'Status',
      minWidth: 50,
      valueGetter: (_value, row) => row.serviceStatus?.name,
      renderCell: (params: GridRenderCellParams) => {
        const name = params.row.serviceStatus?.name;
        return name ? (
          <TablePill tone={toneForStatus(name)} ariaLabel={`Status: ${name}`}>
            {name}
          </TablePill>
        ) : null;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      minWidth: 175,
      valueGetter: (_value, row) => row.createdAt && formatDate(row.createdAt),
    },
    {
      field: 'closedAt',
      headerName: 'Closed Date',
      minWidth: 175,
      valueGetter: (_value, row) => row.closedAt && formatDate(row.closedAt),
    },
    {
      field: 'presentation',
      headerName: 'Presentation',
      minWidth: 175,
    },
  ];

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setIncludeChildRecords(event.target.checked);
  };

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setSelectionModel(newSelection);
  };

  return (
    <Panel
      title="Services"
      titleAdornment={<TablePill tone="neutral">{serviceData.length}</TablePill>}
      collapsible
      bodyPadding={0}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <FormControlLabel
            sx={v4PanelToggleSx}
            control={
              <Switch
                size={SizeProps.Small}
                color={ColorProps.Primary}
                onChange={handleChange}
                checked={includeChildRecords}
              />
            }
            label="Include child records"
          />
          {/* One control, not a button inside a link: nested interactive elements
              are invalid semantics and behave inconsistently from the keyboard.
              The link takes the button's styling instead. */}
          <MuiButton
            component="a"
            href={`/services?locationId=${nodeId}&includeChildRecords=${includeChildRecords}`}
            variant="outlined"
            size="small"
            sx={v4GhostButtonSx}
          >
            Search all services
          </MuiButton>
        </Stack>
      }
    >
      <TableWrapper>
        <DataGrid
          v4Skin
          loading={getServicesUnderNodeId.isLoading || getServicesUnderNodeId.isRefetching}
          density={GridDensityTypes.Compact}
          columns={columns}
          rows={serviceData}
          getRowId={(row) => `${row.relation} ${row.id}`}
          disableColumnFilter={true}
          disableColumnSelector={true}
          disableDensitySelector={true}
          componentsProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              showQuickFilter: false,
            },
          }}
          columnVisibilityModel={{
            subLocationName: includeChildRecords,
          }}
          initialState={{
            sorting: {
              sortModel: [
                { field: 'createdAt', sort: 'desc' },
                { field: GRID_TREE_DATA_GROUPING_FIELD, sort: 'desc' },
              ],
            },
          }}
          treeData={true}
          getTreeDataPath={(row) => [
            row.serviceStatus?.isClosed ? ServiceStatusIsClosed.Closed : ServiceStatusIsClosed.Open,
            `${row.relation} ${row.id}`,
          ]}
          defaultGroupingExpansionDepth={-1}
          groupingColDef={{
            hideDescendantCount: true,
            valueFormatter: (value: string | number) =>
              value === ServiceStatusIsClosed.Open || value === ServiceStatusIsClosed.Closed
                ? value
                : '',
            width: 100,
          }}
          selectionModel={selectionModel}
          onSelectionModelChange={handleSelectionChange}
          hideFooter
          customFooter={() => (
            <CustomFooter rowsSelected={selectionModel.length}>
              {selectionModel.length ? (
                <Stack ml="15px">
                  {selectionModel.length} row{selectionModel.length > 1 ? 's' : ''} selected
                </Stack>
              ) : (
                ''
              )}
              <Stack mr="15px">Total Rows: {serviceData.length}</Stack>
            </CustomFooter>
          )}
        />
      </TableWrapper>
    </Panel>
  );
};
